import type { ReactElement } from 'react';
import { zonedLocalToISO } from '../../lib/datetime';
import { useI18n } from '../../lib/i18n/I18nProvider';
import { Field, Ltr } from '../../ui/primitives';

export interface ScheduleValues {
  registrationOpens: string;
  eventStarts: string;
  eventEnds: string;
  automatic: boolean;
}

export interface ScheduleErrors {
  registrationOpens?: string;
  eventStarts?: string;
  eventEnds?: string;
}

export function validateSchedule(values: ScheduleValues, timezone: string, required = true): ScheduleErrors {
  const errors: ScheduleErrors = {};
  if (required && !values.registrationOpens) errors.registrationOpens = 'required';
  if (required && !values.eventStarts) errors.eventStarts = 'required';
  if (required && !values.eventEnds) errors.eventEnds = 'required';
  if (values.registrationOpens && values.eventStarts && values.registrationOpens >= values.eventStarts) {
    errors.eventStarts = 'after_registration';
  }
  if (values.eventStarts && values.eventEnds && values.eventStarts >= values.eventEnds) {
    errors.eventEnds = 'after_start';
  }
  if (values.registrationOpens && !zonedLocalToISO(values.registrationOpens, timezone)) {
    errors.registrationOpens = 'invalid_timezone';
  }
  if (values.eventStarts && !zonedLocalToISO(values.eventStarts, timezone)) {
    errors.eventStarts = 'invalid_timezone';
  }
  if (values.eventEnds && !zonedLocalToISO(values.eventEnds, timezone)) {
    errors.eventEnds = 'invalid_timezone';
  }
  return errors;
}

export function schedulePayload(values: ScheduleValues, timezone: string): Record<string, unknown> | null {
  const registration = zonedLocalToISO(values.registrationOpens, timezone);
  const starts = zonedLocalToISO(values.eventStarts, timezone);
  const ends = zonedLocalToISO(values.eventEnds, timezone);
  if (!registration || !starts || !ends) return null;
  return {
    registration_opens_at: registration,
    go_live_scheduled_at: starts,
    event_ends_at: ends,
    auto_transition_enabled: values.automatic,
  };
}

function errorText(code: string | undefined, t: ReturnType<typeof useI18n>['t']): string | undefined {
  if (!code) return undefined;
  if (code === 'required') return t('admin.expo.schedule.error.required');
  if (code === 'after_registration') return t('admin.expo.schedule.error.startOrder');
  if (code === 'after_start') return t('admin.expo.schedule.error.endOrder');
  return t('admin.expo.schedule.error.timezone');
}

export function ExpoScheduleFields({
  values,
  onChange,
  timezone,
  errors,
}: {
  values: ScheduleValues;
  onChange: (next: ScheduleValues) => void;
  timezone: string;
  errors: ScheduleErrors;
}): ReactElement {
  const { t } = useI18n();
  const timezoneHint = t('admin.expo.schedule.timezoneHint', { timezone });
  return (
    <div className="expo-schedule">
      <div className="expo-schedule__timeline" aria-label={t('admin.expo.schedule.title')}>
        <div className="expo-schedule__step">
          <span className="expo-schedule__number">1</span>
          <span>{t('admin.expo.schedule.registration')}</span>
        </div>
        <div className="expo-schedule__step">
          <span className="expo-schedule__number">2</span>
          <span>{t('admin.expo.schedule.start')}</span>
        </div>
        <div className="expo-schedule__step">
          <span className="expo-schedule__number">3</span>
          <span>{t('admin.expo.schedule.end')}</span>
        </div>
      </div>
      <div className="expo-schedule__fields">
        <Field
          label={t('admin.expo.schedule.registration')}
          name="registration_opens_at"
          type="datetime-local"
          value={values.registrationOpens}
          onChange={(registrationOpens) => onChange({ ...values, registrationOpens })}
          hint={timezoneHint}
          error={errorText(errors.registrationOpens, t)}
          required
          ltr
        />
        <Field
          label={t('admin.expo.schedule.start')}
          name="go_live_scheduled_at"
          type="datetime-local"
          value={values.eventStarts}
          onChange={(eventStarts) => onChange({ ...values, eventStarts })}
          hint={t('admin.expo.schedule.startHint')}
          error={errorText(errors.eventStarts, t)}
          required
          ltr
        />
        <Field
          label={t('admin.expo.schedule.end')}
          name="event_ends_at"
          type="datetime-local"
          value={values.eventEnds}
          onChange={(eventEnds) => onChange({ ...values, eventEnds })}
          hint={timezoneHint}
          error={errorText(errors.eventEnds, t)}
          required
          ltr
        />
      </div>
      <label className="expo-schedule__automation">
        <input
          type="checkbox"
          checked={values.automatic}
          onChange={(event) => onChange({ ...values, automatic: event.target.checked })}
        />
        <span>
          <strong>{t('admin.expo.schedule.automatic')}</strong>
          <small>{t('admin.expo.schedule.automaticHint')}</small>
        </span>
      </label>
      <p className="expo-schedule__timezone">
        {t('admin.expo.schedule.timezone')}: <Ltr>{timezone}</Ltr>
      </p>
    </div>
  );
}
